import { defineStore, storeToRefs } from 'pinia'
import { ref, computed, ComputedRef } from 'vue'
import { useSubscriptionStore } from './SubscriptionStore'
import { OfferProductId, Plan, ProductId } from '@/types'
import { usePlanStore } from './PlanStore'
import { useCouponStore } from './CouponStore'

export const useOfferStore = defineStore('OfferStore', () => {
  // the offer ID of the current page
  const offerId = ref('')
  const subscriptionStore = useSubscriptionStore()
  const planStore = usePlanStore()
  const couponStore = useCouponStore()

  const {
    currentPromotionalPlanProducts,
    hasActivePausedSubscription,
    hasCurrentPromotionalSubscription,
    hasExpiredSubscription,
    mostRecentSubscriptionProduct,
    mostRecentSubscriptionInterval,
    promotionalSubscriptionProducts
  } = storeToRefs(subscriptionStore)

  const { plans, standardPlans } = storeToRefs(planStore)
  const { codeIsRedeemable } = couponStore

  const currentOffers = computed(() => [
    {
      id: OfferProductId.CoProducerOutputFxPromo,
      isEligible:
        hasActivePausedSubscription.value &&
        mostRecentSubscriptionProduct.value === ProductId.Arcade &&
        !promotionalSubscriptionProducts.value?.includes(OfferProductId.CoProducerOutputFxPromo) &&
        codeIsRedeemable('loyalty-offer-existing-arcade-subs'),
      planCodes: ['250204-co-pro-output-fx-promo-m'],
      couponCode: 'loyalty-offer-existing-arcade-subs',
      paymentProductDisplay: 'Arcade, Co-Producer, Output FX bundle',
      offerProduct: ProductId.OutputOne,
      canUpdatePayment: false
    },
    {
      id: OfferProductId.OutputOneResubscribePromo,
      isEligible:
        hasExpiredSubscription.value && mostRecentSubscriptionProduct.value === ProductId.Arcade && codeIsRedeemable('output-one-launch-loyalty-offer-2025'),
      planCodes: ['241203-output-bundle-m', '241203-output-bundle-y'],
      couponCode: 'output-one-launch-loyalty-offer-2025',
      offerProduct: ProductId.OutputOne,
      canUpdatePayment: true
    }
  ])

  const setOfferId = (id: string) => {
    offerId.value = id
  }

  // The offer for the current page
  const activeOffer = computed(() => currentOffers.value.find(offer => offer.id === offerId.value))
  const plansForActiveOffer: ComputedRef<Plan[]> = computed(() => plans.value.filter(({ code }: Plan) => activeOfferPlanCodes.value?.includes(code)))
  const activeOfferCoupons = computed(() => activeOffer.value?.couponCode)
  const activeOfferPlanCodes = computed(() => activeOffer.value?.planCodes)
  const activeOfferProduct = computed(() => activeOffer.value?.offerProduct)

  const currentOfferCoupons = computed(() => currentOffers.value.map(offer => offer.couponCode))

  // first active offer for which user is currently eligible
  const eligibleOffer = computed(() => currentOffers.value?.find(offer => offer.isEligible))
  const eligibleOfferId = computed(() => eligibleOffer.value?.id)
  const hasEligibleOffer = computed(() => !!eligibleOffer.value && !hasCurrentPromotionalSubscription.value)

  // is this user eligible for this active offer?
  const isEligibleForActiveOffer = computed(() => activeOffer.value?.isEligible && !hasCurrentPromotionalSubscription.value)

  // return the plan that matches user's last plan interval
  const activeOfferPlanFromLastInterval = computed(() => {
    const matchInterval = plansForActiveOffer.value?.find((plan: Plan) => {
      return plan.interval === mostRecentSubscriptionInterval.value
    })
    const [first] = plansForActiveOffer.value
    return matchInterval || first
  })

  // Return Offer Products for which a user already has
  const promotionalOfferProduct = computed(() => {
    return currentPromotionalPlanProducts.value.map((productId: string) => {
      const offerMatch = currentOffers.value.find(offer => offer.id === productId)

      return offerMatch?.offerProduct
    })
  })
  const activeOfferAllowPaymentUpdates = computed(() => activeOffer.value?.canUpdatePayment)

  const activeOfferId = computed(() => activeOffer.value?.id)
  const activeOfferPaymentProductDisplay = computed(() => activeOffer.value?.paymentProductDisplay)

  const postPromotionalOfferPlan = computed(() => {
    const { mostRecentSubscriptionInterval } = storeToRefs(subscriptionStore)
    const plan = standardPlans.value.find(
      (plan: Plan) => plan.planProduct === activeOfferProduct.value && plan.interval === mostRecentSubscriptionInterval.value
    )
    return plan
  })

  const postPromotionalOfferPlanInterval = computed(() => postPromotionalOfferPlan.value?.interval)

  return {
    activeOffer,
    activeOfferAllowPaymentUpdates,
    activeOfferCoupons,
    activeOfferPaymentProductDisplay,
    activeOfferPlanFromLastInterval,
    activeOfferProduct,
    activeOfferId,
    currentOfferCoupons,
    currentOffers,
    eligibleOffer,
    eligibleOfferId,
    hasEligibleOffer,
    isEligibleForActiveOffer,
    offerId,
    plansForActiveOffer,
    promotionalOfferProduct,
    setOfferId,
    postPromotionalOfferPlan,
    postPromotionalOfferPlanInterval
  }
})
